import React, { useMemo } from 'react';

import { Flex, Text, Button } from '@opendoor/bricks/core';

import { OwnersForm } from 'sellDirect/components/closingDash/contract/OwnersForm';
import { HoaDetailsForm } from 'sellDirect/components/closingDash/contract/HoaDetailsForm';
import { CloseDatePicker } from 'sellDirect/components/closingDash/contract/CloseDatePicker';

import { validateContractFormInput } from 'sellDirect/helpers/closingDash/contracts';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import { PreContractDetailsFormState } from 'sellDirect/components/closingDash/contract/SetUpPurchaseContractForm';
import { LocalDate } from '@js-joda/core';

const NEXT_BUTTON_MAX_WIDTH = 327;

interface SetUpPurchaseContractFormContentProps {
  shouldSeeAgentExperience: boolean;
  closingTasks: ClosingTasks;
  loading: boolean;
  resetableForm: IResetableFormRenderProps<PreContractDetailsFormState>;
}

/**
 * Form content for the form that handles setting up the contract form
 *
 * @param shouldSeeAgentExperience - Whether the seller should see the agent experience
 * @param closingTasks - Current closing tasks
 * @param loading - Whether we're currently loading data (i.e. submitting)
 * @param resetableForm - The resetableForm object
 *
 * component-test-check-ignore
 */
export const SetUpPurchaseContractFormContent: React.FC<SetUpPurchaseContractFormContentProps> = ({
  shouldSeeAgentExperience,
  closingTasks,
  loading,
  resetableForm,
}) => {
  const availableCloseDates = closingTasks.setCloseDate?.taskable.availableDates;

  const { isEdited, formState, onSubmit, onUpdate } = resetableForm;

  const isFormValid = useMemo(
    () => validateContractFormInput(isEdited, formState),
    [isEdited, formState],
  );

  return (
    <Flex flexDir="column" gridGap="9" pb={[null, null, 11]}>
      <Text size="s1">
        Answer a few questions to set up {shouldSeeAgentExperience ? 'the' : 'your'} contract. You
        can edit this later.
      </Text>

      <OwnersForm disabled={loading} resetableForm={resetableForm} />

      <HoaDetailsForm
        shouldSeeAgentExperience={shouldSeeAgentExperience}
        resetableForm={resetableForm}
        disabled={loading}
      />

      <CloseDatePicker
        availableCloseDates={availableCloseDates || []}
        closeDate={formState.closeDate ? LocalDate.parse(formState.closeDate) : undefined}
        onUpdate={(value) => onUpdate('closeDate', value.toString())}
        disabled={loading}
        shouldSeeAgentExperience={shouldSeeAgentExperience}
      />

      <Flex width={['100%', null, NEXT_BUTTON_MAX_WIDTH]}>
        <Button
          analyticsName="cake-save-contract-form"
          disabled={!isFormValid || loading}
          loading={loading}
          onClick={onSubmit}
          aria-label="Go to Next step and save purchase contract form"
        >
          Next
        </Button>
      </Flex>
    </Flex>
  );
};
